import { FC, ReactNode, useState, useCallback, MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import slugify from 'dibs-slugify/exports/slugify';

import { trackEvent } from 'dibs-tracking';
import {
    MORE_ITEMS,
    ITEM_DETAILS,
    ABOUT_SELLER,
    labelMap,
} from '../../helpers/soldPdpRedesignConstants';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { Link } from 'dibs-elements/exports/Link';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { useTab } from 'dibs-elements/exports/useTab';
import { PdpRespAboutModule } from '../../PdpRespAboutModule/PdpRespAboutModule';
import { PdpRespStorytellingTab } from '../../PdpRespStorytellingModule/PdpRespStorytellingTab';
import {
    PdpSharedStorytellingTrackingContextProvider,
    PdpSharedStorytellingTrackingWrapper,
} from '../../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';
import { PdpRespDealerDetails } from '../../PdpRespDealerDetails/PdpRespDealerDetails';
import { getSelectedSku } from '../../helpers/getSelectedSku';

import { PdpRespDetailsTabs_viewer$key } from './__generated__/PdpRespDetailsTabs_viewer.graphql';
import { PdpRespDetailsTabs_user$key } from './__generated__/PdpRespDetailsTabs_user.graphql';
import { PdpRespDetailsTabs_item$key } from './__generated__/PdpRespDetailsTabs_item.graphql';

import styles from './PdpRespDetailsTabs.scss';

const viewerFragment = graphql`
    fragment PdpRespDetailsTabs_viewer on Viewer {
        ...PdpRespDealerDetails_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespDetailsTabs_user on User {
        ...PdpRespAboutModule_user
        ...PdpRespDealerDetails_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespDetailsTabs_item on Item {
        skus {
            serviceId
            ...PdpRespDealerDetails_sku @include(if: $isMultiSku)
        }
        storyTellingModule {
            tabs {
                tabType
                tabTitle
                ...PdpRespStorytellingTab_storyTellingTab
            }
        }
        seller {
            isAccessible
        }
        ...PdpRespAboutModule_item
        ...PdpRespDealerDetails_item
    }
`;

// TODO: replace with State type
type ReduxState = {
    relayVariables: {
        variables: {
            isClient: boolean;
            isMultiSku: boolean;
        };
    };
};

const CustomTab: FC<{ name: string; dataTn?: string; children: ReactNode }> = ({
    name,
    dataTn,
    children,
}) => {
    const { tabCallbackRef, handleClick, isActive } = useTab(name);
    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            handleClick();
            trackEvent(
                {
                    category: 'product',
                    action: 'sold pdp tab clicked',
                    label: labelMap[name],
                },
                event
            );
        },
        [handleClick, name]
    );

    return (
        <li className={styles.navTabItem}>
            <Link
                buyerLinkType="linkNoDecoration"
                linkRef={tabCallbackRef}
                className={classnames(styles.navTabLink, { [styles.active]: isActive })}
                onClick={onClick}
                dataTn={dataTn}
            >
                {children}
            </Link>
        </li>
    );
};

export const PdpRespDetailsTabs: FC<{
    selectedSkuId: string | null;
    viewer: PdpRespDetailsTabs_viewer$key;
    user: PdpRespDetailsTabs_user$key;
    item: PdpRespDetailsTabs_item$key;
}> = ({ selectedSkuId, ...props }) => {
    const viewer = useFragment(viewerFragment, props.viewer);
    const user = useFragment(userFragment, props.user);
    const item = useFragment(itemFragment, props.item);

    const [activeTab, setActiveTab] = useState(MORE_ITEMS);
    const { isClient, isMultiSku } = useSelector(
        (state: ReduxState) => state.relayVariables.variables
    );

    const tabs = item?.storyTellingModule?.tabs || [];
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const isSellerAccessible = item?.seller?.isAccessible;

    return (
        <PdpSharedStorytellingTrackingContextProvider>
            <CurrencyProvider
                render={({ currency }: $TSFixMe) => (
                    <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                        <TabList fullWidth customTab indicatorPadding="xSmall">
                            {[
                                [
                                    MORE_ITEMS,
                                    <CustomTab
                                        key={MORE_ITEMS}
                                        name={MORE_ITEMS}
                                        dataTn="more-items-tab"
                                    >
                                        <FormattedMessage
                                            id="PdpRespDetailsTabs.moreItems"
                                            defaultMessage="More Items"
                                        />
                                    </CustomTab>,
                                ],
                                [
                                    ITEM_DETAILS,
                                    <CustomTab
                                        key={ITEM_DETAILS}
                                        name={ITEM_DETAILS}
                                        dataTn="item-details-tab"
                                    >
                                        <FormattedMessage
                                            id="PdpRespDetailsTabs.itemDetails"
                                            defaultMessage="Item Details"
                                        />
                                    </CustomTab>,
                                ],
                                ...tabs.reduce<[string, ReactNode][]>((acc, tab) => {
                                    if (tab) {
                                        acc.push([
                                            tab.tabType,
                                            <CustomTab
                                                key={tab.tabTitle}
                                                name={tab.tabType}
                                                dataTn={`${slugify(tab.tabTitle)}-tab`}
                                            >
                                                {tab.tabTitle}
                                            </CustomTab>,
                                        ]);
                                    }
                                    return acc;
                                }, []),
                                isSellerAccessible && [
                                    ABOUT_SELLER,
                                    <CustomTab
                                        key={ABOUT_SELLER}
                                        name={ABOUT_SELLER}
                                        dataTn="about-the-seller-tab"
                                    >
                                        <FormattedMessage
                                            id="PdpRespDetailsTabs.aboutSeller"
                                            defaultMessage="About the Seller"
                                        />
                                    </CustomTab>,
                                ],
                            ]}
                        </TabList>
                        <div className={styles.panelsWrapper}>
                            <TabPanels renderOnInactive>
                                {[
                                    [MORE_ITEMS, null],
                                    [
                                        ITEM_DETAILS,
                                        <PdpRespAboutModule
                                            isClient={isClient}
                                            currency={currency}
                                            selectedSkuId={selectedSkuId}
                                            item={item}
                                            user={user}
                                            //Because of the current inactive tab rendering we need this workaraound to make expanding area functional.
                                            showExpandingArea={activeTab === ITEM_DETAILS}
                                            dataTn="item-details-tab-content"
                                        />,
                                    ],
                                    ...tabs.reduce<[string, ReactNode][]>((acc, tab) => {
                                        if (tab) {
                                            acc.push([
                                                tab.tabType,
                                                <PdpSharedStorytellingTrackingWrapper
                                                    key={tab.tabTitle}
                                                >
                                                    <PdpRespStorytellingTab
                                                        storyTellingTab={tab}
                                                        clearBackground={false}
                                                        //Because of the current inactive tab rendering we need this workaraound to make expanding area functional.
                                                        showExpandingArea={
                                                            activeTab === tab.tabType
                                                        }
                                                        dataTn={`${slugify(
                                                            tab.tabTitle
                                                        )}-tab-content`}
                                                    />
                                                </PdpSharedStorytellingTrackingWrapper>,
                                            ]);
                                        }
                                        return acc;
                                    }, []),
                                    isSellerAccessible && [
                                        ABOUT_SELLER,
                                        <PdpRespDealerDetails
                                            user={user}
                                            item={item}
                                            sku={isMultiSku && selectedSku ? selectedSku : null}
                                            viewer={viewer}
                                            currency={currency}
                                            isClient={isClient}
                                            dataTn="about-the-seller-tab-content"
                                        />,
                                    ],
                                ]}
                            </TabPanels>
                        </div>
                    </TabProvider>
                )}
            />
        </PdpSharedStorytellingTrackingContextProvider>
    );
};
